import { create } from 'zustand';
import { authHeaders } from '@/api/persist';
import { isWritePending } from '@/state/writeQueue';
import { PERSISTABLE_ENTITIES } from '@/state/persistableEntities';
import type { NurtureTouch, NurtureInbound, NurtureSegment } from '@/domain/nurtureFlow';
import type {
  ActivityFeedEntry,
  Agent,
  Artist,
  Booking,
  BookingFlag,
  ConversationEntry,
  CustomerRecord,
  Deposit,
  DesignIntent,
  FutureReadinessSignal,
  MedicalProfile,
  StudioId,
  TattooStyle,
  Piece,
  SelfBookLink,
  SessionPhotoKind,
  Studio,
  TailRecord,
  MarketingCampaign,
  ConfirmationCall,
  InternalCommsChannel,
  InternalCommsMessage,
  InternalDmThread,
  RebookAttribution,
  RebookFollowupTask,
  HealingCheckTask,
  NoRebookFlag,
  NoDepositDeclineFlag,
  PaybyExtensionRequest,
  SweeperOutcome,
  RebookOutcome,
  OverrideRequest,
  RescheduleReason,
  Waiver,
  SalePayment,
  PayoutRecord,
  CashLedger,
  MerchItem,
  DocketLine,
  SettlementLanding,
  DepositLanding,
  SettlementLump,
  DayPack,
  TopupPlan,
} from '@/domain/types';
import { DEFAULT_ROLE_PERMISSIONS, type RolePermission } from '@/domain/permissions';
import {
  DEFAULT_REPORTING_TARGETS,
  type ReportingTargetsConfig,
  type ArtistWeeklyTarget,
} from '@/data/reportingTargets';
import type { SequenceState } from '@/domain/sequenceFlow';
import type { ReconResolution } from '@/domain/reconResolutionFlow';
import { seedNow, seedSilent } from '@/domain/seedContext';
import { useSimulationStore } from '@/state/simulationStore';
import { stableHash } from '@/utils/seededRandom';
import { fixtureBundleToState } from '@/data/fixtureBundleToState';
import { withoutWriteThrough } from './persistenceBridge';
import { isStoreFallbackEnabled } from '@/api/apiMode';
import { applyNoDateRelease, isSolid, nextThreadTimestamp, resetCadence, threadLastTimestamp } from '@atink/domain';
import { useConnectionStatus } from '@/state/connectionStatus';
import { deriveTodayStats } from '@/data/todayStats';

export interface BrainState {
  initialised: boolean;
  /** True when a served-build bootstrap could not reach the API (gate OFF). */
  bootUnreachable: boolean;
  customers: Record<string, CustomerRecord>;
  customerIdsByPhone: Record<string, string>;
  bookings: Record<string, Booking>;
  deposits: Record<string, Deposit>;
  /** Agent-built pay-over-time deposit plans (tv-w98); keyed by plan id. */
  topupPlans: Record<string, TopupPlan>;
  waivers: Record<string, Waiver>;
  pieces: Record<string, Piece>;
  futureSignals: Record<string, FutureReadinessSignal>;
  selfBookLinks: Record<string, SelfBookLink>;
  /** Marketing spend sources (Prompt F): funnel ad-spend + per-campaign cost-per-deposit. */
  marketingCampaigns: Record<string, MarketingCampaign>;
  confirmationCalls: Record<string, ConfirmationCall>;
  internalComms: Record<string, InternalCommsChannel>;
  internalDmThreads: Record<string, InternalDmThread>;
  nurtureTouches: Record<string, NurtureTouch>;
  nurtureInbounds: Record<string, NurtureInbound>;
  /** Editable per-segment nurture copy (overrides the defaults). Keyed by segment. */
  nurtureTemplates: Record<string, string>;
  rebookFollowupTasks: Record<string, RebookFollowupTask>;
  healingCheckTasks: Record<string, HealingCheckTask>;
  /** No-rebook mid-piece Ops flags (tv-3gl); keyed by flag id. Read by tv-v4p. */
  noRebookFlags: Record<string, NoRebookFlag>;
  /** Declined no-deposit override flags (tv-bvx); keyed `ndf-${customerId}`. A2 writes, C2 reads/resolves. */
  noDepositDeclineFlags: Record<string, NoDepositDeclineFlag>;
  /** W5 i5: inside-7-day pay-by extension requests awaiting Ops. */
  paybyExtensionRequests: Record<string, PaybyExtensionRequest>;
  /** B7: the Sweeper's call outcomes, keyed `swo-<gapKey>-<customerId>`. */
  sweeperOutcomes: Record<string, SweeperOutcome>;
  overrideRequests: Record<string, OverrideRequest>;
  /** Reconcile exception resolutions (K1): resolver note + attachment + approval trail, keyed by row id. */
  reconResolutions: Record<string, ReconResolution>;
  sequenceState: Record<string, SequenceState>;
  /** Make-sale records written at checkout (E1); read by E3 reconciliation. */
  salePayments: Record<string, SalePayment>;
  /** Signed cash-envelope handovers (E2); keyed by record id. */
  payoutRecords: Record<string, PayoutRecord>;
  /** Per store, per day float/cash ledger (E2); keyed `${studioId}:${dayIso}`. */
  cashLedgers: Record<string, CashLedger>;
  /** Per-studio merch/aftercare catalogue (E1); keyed by item id. */
  merchItems: Record<string, MerchItem>;
  /** Terminal / deposit-sheet reference echoes (E3); read by reconciliation. */
  docketLines: Record<string, DocketLine>;
  /** Card-takings settlement landings (E3 cash-timing strip); keyed by id. */
  settlementLandings: Record<string, SettlementLanding>;
  /** Deposit bank landings (amendment 11 deposit-recon lane); keyed by id. */
  depositLandings: Record<string, DepositLanding>;
  /** Bank settlement lumps (E4); split by reference. Keyed by id. */
  settlementLumps: Record<string, SettlementLump>;
  /** Frozen end-of-day packs (E4); keyed `${studioId}:${dayIso}`. */
  dayPacks: Record<string, DayPack>;
  studios: Record<string, Studio>;
  artists: Record<string, Artist>;
  agents: Record<string, Agent>;
  rolePermissions: RolePermission[];
  /** Editable weekly targets/thresholds (Settings Targets tab). Config, not fixture data. */
  reportingTargets: ReportingTargetsConfig;
  tailRecords: TailRecord[];
  activityFeed: ActivityFeedEntry[];
  todayStats: {
    deposits: number;
    callsRecovered: number;
    crossStudioRoutes: number;
    inChairRebookings: number;
    newCustomers: number;
    networkRevenueAud: number;
  };

  loadFixtures: () => void;
  /** Re-derive `todayStats` from the current records (run after the journey replay lands). */
  recomputeTodayStats: () => void;
  upsertNurtureTouch: (t: NurtureTouch) => void;
  upsertNurtureInbound: (i: NurtureInbound) => void;
  resolveNurtureInbound: (id: string, atIso: string) => void;
  setNurtureTemplate: (segment: NurtureSegment, body: string) => void;
  upsertCustomer: (c: CustomerRecord) => void;
  /**
   * Persist a customer's medical profile. The store owns the audit stamp —
   * callers pass the clinical fields only; `lastUpdatedAt` / `lastUpdatedBy`
   * are set here (server-side in production, in-store for the prototype).
   */
  updateMedicalProfile: (
    customerId: string,
    profile: Omit<MedicalProfile, 'lastUpdatedAt' | 'lastUpdatedBy'>,
    updatedBy?: string,
  ) => void;
  /**
   * Shallow-merge preference keys onto a customer (persist). No-op if the
   * customer is missing. Later scopes artist filtering.
   */
  setCustomerPreferences: (
    customerId: string,
    patch: {
      preferredArtistId?: string;
      preferredStyle?: TattooStyle;
      preferredStudioId?: StudioId;
    },
  ) => void;
  /**
   * Merge a patch onto a customer's designIntent (the call brief), creating a
   * minimal object if none exists. No-op if the customer is missing.
   */
  updateDesignIntent: (customerId: string, patch: Partial<DesignIntent>) => void;
  addConversationEntry: (customerId: string, entry: ConversationEntry) => void;
  addRapportNote: (customerId: string, note: string, author?: string, atIso?: string) => void;
  addFutureReadinessSignal: (signal: FutureReadinessSignal) => void;
  upsertBooking: (b: Booking) => void;
  /**
   * Keep-original reschedule (15 Jul ruling), applied atomically in one `set`.
   * The original is preserved as `superseded` and a NEW booking is created for
   * the new slot, linked both ways; the paid deposit is repointed to the new
   * record. Orchestrated by `rescheduleBooking` in src/domain/rescheduleFlow.ts.
   */
  applyReschedule: (
    args: {
      originalId: string;
      reason: RescheduleReason;
      note?: string;
      by: string;
      at: string;
    } & (
      | { mode: 'move'; newBookingId: string; artistId: string; newStartIso: string }
      | { mode: 'release' }
    ),
  ) => void;
  /** Persist an agent-dismissed flag *suggestion* (derived flags aren't stored). */
  dismissFlagSuggestion: (bookingId: string, kind: BookingFlag) => void;
  upsertArtist: (a: Artist) => void;
  upsertStudio: (s: Studio) => void;
  upsertAgent: (a: Agent) => void;
  setRolePermissions: (next: RolePermission[]) => void;
  /** Patch one artist's weekly targets (merges over the current resolved target). */
  setArtistTarget: (artistId: string, patch: Partial<ArtistWeeklyTarget>) => void;
  /** Set a studio's weekly reviews target. */
  setStudioReviewsTarget: (studioId: string, reviewsPerWeek: number) => void;
  upsertDeposit: (d: Deposit) => void;
  /** Upsert an instalment (top-up) plan, keyed by plan id (tv-w98). */
  upsertTopupPlan: (p: TopupPlan) => void;
  /** Upsert a waiver; registers its id on the owning customer's `waivers` array. */
  upsertWaiver: (w: Waiver) => void;
  /**
   * Mirror server truth a live view fetched (tv-2na.11.4/.5) into the store,
   * forward-only and WITHOUT write-through. See `absorbServerTruthInto`.
   */
  absorbServerTruth: (server: ServerTruth) => void;
  /**
   * Fold the server's copy of changed customers in, without writing them back (B2).
   * `fetchStartedAtMs` (a live refresh passes it) also lets moved server rows go.
   */
  absorbServerCustomers: (server: Record<string, CustomerRecord>, fetchStartedAtMs?: number) => void;
  /**
   * Remove a customer the SERVER already deleted (a DM lead merged by a link),
   * WITHOUT write-through: no DELETE (already gone) and no later PUT that would
   * recreate it (tv-isee).
   */
  dropServerDeletedCustomer: (id: string) => void;
  /**
   * B8: fold the server's sequence records for `ids` in without write-through — a
   * row the server no longer has is dropped (the lead left the sequences), unless
   * this tab still has an unsaved write for it.
   */
  absorbServerSequenceState: (
    ids: readonly string[],
    server: Record<string, SequenceState>,
    isPending: (id: string) => boolean,
  ) => void;
  upsertPiece: (p: Piece) => void;
  addPieceProgressPhoto: (
    pieceId: string,
    photo: {
      url: string;
      caption?: string;
      bookingId?: string;
      capturedAt?: string;
      kind?: SessionPhotoKind;
    },
  ) => void;
  upsertFutureSignal: (s: FutureReadinessSignal) => void;
  upsertSelfBookLink: (l: SelfBookLink) => void;
  upsertConfirmationCall: (c: ConfirmationCall) => void;
  setReconResolution: (r: ReconResolution) => void;
  removeReconResolution: (rowId: string) => void;
  appendActivity: (a: ActivityFeedEntry) => void;
  incStat: (key: keyof BrainState['todayStats'], by?: number) => void;
  sendChannelMessage: (
    channelId: string,
    body: string,
    opts?: {
      authorId?: string;
      parentMessageId?: string;
      mentions?: string[];
      customerRefId?: string;
      timestamp?: string;
    },
  ) => void;
  sendDmMessage: (
    threadId: string,
    body: string,
    opts?: {
      authorId?: string;
      mentions?: string[];
      customerRefId?: string;
      timestamp?: string;
      /** Persisted attachments, e.g. the W5 Ops-row link `{kind:'link', url:'/ops?item=<id>'}`. */
      attachments?: InternalCommsMessage['attachments'];
    },
  ) => void;
  openOrCreateDm: (participantIds: string[], creatorId: string, nowIso: string) => string;
  /** Create a new internal-comms channel and return its id. The creator is always a member. */
  createChannel: (
    name: string,
    memberIds: string[],
    creatorId: string,
    opts?: { description?: string; studioId?: StudioId },
  ) => string;
  addReaction: (
    scope: { kind: 'channel' | 'dm'; containerId: string },
    messageId: string,
    emoji: string,
    byPersonId: string,
  ) => void;
  addAutoPing: (
    channelId: string,
    body: string,
    opts?: { customerRefId?: string; timestamp?: string; mentions?: string[] },
  ) => void;
  /** Record the in-chair rebook conversation outcome on a completed booking. */
  setRebookOutcome: (
    bookingId: string,
    outcome: RebookOutcome,
    attribution: RebookAttribution,
  ) => void;
  /** Upsert a sales-team follow-up task created from Outcome C. */
  upsertRebookFollowup: (task: RebookFollowupTask) => void;
  upsertHealingCheck: (task: HealingCheckTask) => void;
  /** Upsert a no-rebook mid-piece Ops flag (tv-3gl); read by the Ops feeder (tv-v4p). */
  upsertNoRebookFlag: (flag: NoRebookFlag) => void;
  /** Upsert a declined no-deposit override flag (tv-bvx); A2 writes, C2 reads/resolves. */
  upsertNoDepositDeclineFlag: (flag: NoDepositDeclineFlag) => void;
  upsertPaybyExtensionRequest: (req: PaybyExtensionRequest) => void;
  upsertSweeperOutcome: (outcome: SweeperOutcome) => void;
  /** Upsert a no-deposit override request (created + resolved via approvals.ts). */
  upsertOverrideRequest: (request: OverrideRequest) => void;
  /** Upsert a make-sale record (E1 checkout). */
  upsertSalePayment: (payment: SalePayment) => void;
  /** Upsert a payout record (E2 sign-off). */
  upsertPayoutRecord: (record: PayoutRecord) => void;
  /** Upsert a day's cash ledger (E2), keyed `${studioId}:${dayIso}`. */
  upsertCashLedger: (ledger: CashLedger) => void;
  /** Upsert a merch/aftercare catalogue item (E1). */
  upsertMerchItem: (item: MerchItem) => void;
  /** Upsert a frozen day-close pack (E4); keyed `${studioId}:${dayIso}`. */
  upsertDayPack: (pack: DayPack) => void;
  /** Upsert a terminal docket / deposit-sheet line (W7 n1: the simulated terminal echo). */
  upsertDocketLine: (line: DocketLine) => void;
  /** Upsert a bank settlement lump (W7 n1: the simulated bank batch at day close). */
  upsertSettlementLump: (lump: SettlementLump) => void;
  /** Upsert a customer's sequence-clock state (replaces the existing entry). */
  setSequenceState: (customerId: string, state: SequenceState) => void;
  /** Shallow-merge a patch onto a customer's sequence state (creates stage 1 if none). */
  patchSequenceState: (customerId: string, patch: Partial<SequenceState>) => void;
  /** Remove a customer's sequence-clock state entirely. */
  clearSequenceState: (customerId: string) => void;
  /**
   * Mark a sales-team follow-up resolved. Side-effect: walks back to the
   * source booking and patches its rebookOutcome.resolvedOutcome.
   */
  resolveRebookFollowup: (
    taskId: string,
    resolution: { outcome: 'booked' | 'declined' | 'noContact'; resolvedAt: string },
  ) => void;
  reset: () => void;
}

/** "Now" for the app: the replay timestamp while seeding, else the sim clock. */
function nowIso(): string {
  return seedNow() ?? useSimulationStore.getState().clockIso;
}

/**
 * A progress photo's id. Derived, never random, so replaying the same capture
 * against the same sim clock mints the same id and snapshots compare equal.
 *
 * The identity of a photo is the piece it hangs off, the instant it was
 * captured, and what it actually IS (its url plus how it was filed). An artist
 * snapping 2-3 shots in one action stamps them all with the same `capturedAt`,
 * so the url/kind hash is what keeps them apart. `taken` is the last-resort
 * guard: if that tuple has already been filed on this piece (the same picture
 * captured twice at the same instant, or a 32-bit hash collision) the id gets an
 * ordinal, which still depends only on prior state and not on the wall clock.
 */
function progressPhotoId(
  pieceId: string,
  capturedAt: string,
  url: string,
  kind: SessionPhotoKind | undefined,
  taken: ReadonlySet<string>,
): string {
  // Readable stamp: yyyymmddHHmmss, offset dropped (the hash below carries it).
  const stamp = capturedAt.replace(/\D/g, '').slice(0, 14);
  const digest = stableHash(`${capturedAt}|${kind ?? 'progress'}|${url}`).toString(36);
  const base = `pp-${pieceId}-${stamp}-${digest}`;
  if (!taken.has(base)) return base;
  let n = 2;
  while (taken.has(`${base}-${n}`)) n += 1;
  return `${base}-${n}`;
}

const emptyStats = {
  deposits: 0,
  callsRecovered: 0,
  crossStudioRoutes: 0,
  inChairRebookings: 0,
  newCustomers: 0,
  networkRevenueAud: 0,
};

/**
 * In-flight hydration guard (tv-1vh.15). On boot, <App> and <PersonaSelector>
 * both fire `if (!initialised) loadFixtures()` effects (×React StrictMode's
 * double-invoke) while the async bootstrap fetch is still pending — so
 * `initialised` is false for all of them and each would run a full re-hydration.
 * Every re-hydrate after the persistence bridge is installed re-PUTs the whole
 * store (~600 rows), which now emits an outbox event per row → a WS refetch
 * storm. This flag coalesces those concurrent calls into a single hydration;
 * it clears on completion so an intentional reset can still re-hydrate.
 */
let hydrating = false;

/**
 * Served-build bootstrap retry (gate OFF). When /api/bootstrap can't be reached
 * we do NOT resurrect fixtures — we leave the store empty and re-attempt on a
 * backoff until the API answers, so the stage self-heals without ghost data.
 */
const BOOT_RETRY_BASE_MS = 3_000;
const BOOT_RETRY_CAP_MS = 30_000;
let bootRetryTimer: ReturnType<typeof setTimeout> | null = null;
let bootAttempts = 0;

function scheduleBootstrapRetry(): void {
  if (bootRetryTimer) return;
  const delay = Math.min(BOOT_RETRY_BASE_MS * 2 ** bootAttempts, BOOT_RETRY_CAP_MS);
  bootAttempts += 1;
  bootRetryTimer = setTimeout(() => {
    bootRetryTimer = null;
    useBrainStore.getState().loadFixtures();
  }, delay);
}

/**
 * The store key for a self-book link token. A link minted in the app is keyed by
 * its token, but a hydrated row is keyed by its server slug (a seeded link:
 * `sbl-pat-universal` for token `pat-universal-4k2p`). Writing by token alone
 * would fork a second copy of a seeded link that no screen reads and that the
 * bridge would PUT as a new row (B5, 7fr8). Falls back to the token itself.
 */
export function selfBookLinkKey(links: Record<string, SelfBookLink>, token: string): string {
  if (links[token]?.token === token) return token;
  for (const [key, l] of Object.entries(links)) if (l.token === token) return key;
  return token;
}

/** The self-book link with this token, whatever its store key. */
export function selfBookLinkByToken(links: Record<string, SelfBookLink>, token: string): SelfBookLink | undefined {
  return links[selfBookLinkKey(links, token)];
}

export const useBrainStore = create<BrainState>((set, get) => ({
  initialised: false,
  bootUnreachable: false,
  customers: {},
  customerIdsByPhone: {},
  bookings: {},
  deposits: {},
  topupPlans: {},
  waivers: {},
  pieces: {},
  futureSignals: {},
  selfBookLinks: {},
  marketingCampaigns: {},
  confirmationCalls: {},
  internalComms: {},
  internalDmThreads: {},
  nurtureTouches: {},
  nurtureInbounds: {},
  nurtureTemplates: {},
  rebookFollowupTasks: {},
  healingCheckTasks: {},
  noRebookFlags: {},
  noDepositDeclineFlags: {},
  paybyExtensionRequests: {},
  sweeperOutcomes: {},
  overrideRequests: {},
  reconResolutions: {},
  sequenceState: {},
  salePayments: {},
  payoutRecords: {},
  cashLedgers: {},
  merchItems: {},
  docketLines: {},
  settlementLandings: {},
  depositLandings: {},
  settlementLumps: {},
  dayPacks: {},
  studios: {},
  artists: {},
  agents: {},
  rolePermissions: DEFAULT_ROLE_PERMISSIONS,
  reportingTargets: DEFAULT_REPORTING_TARGETS,
  tailRecords: [],
  activityFeed: [],
  todayStats: { ...emptyStats },

  loadFixtures: () => {
    // Coalesce concurrent boot invocations into a single hydration (tv-1vh.15 —
    // see the `hydrating` flag above). Without this, App + PersonaSelector
    // (×StrictMode) each re-hydrate and the bridge re-PUTs the whole store.
    if (hydrating) return;
    hydrating = true;
    // Phase-1 backend wiring: hydrate from GET /api/bootstrap (the api serves
    // the FixtureBundle — DB-backed collections from Postgres, the rest passed
    // through from the snapshot until their domain migrations land). Falls
    // back to the in-bundle fixtures when no backend is reachable, so the SPA
    // still runs standalone (vitest, story tests, `vite` alone).
    void (async () => {
     try {
      let f: import('@/data/fixtures').FixtureBundle;
      try {
        const res = await fetch('/api/bootstrap', { headers: { ...authHeaders() } });
        if (!res.ok) throw new Error(`bootstrap ${res.status}`);
        f = (await res.json()) as import('@/data/fixtures').FixtureBundle;
        console.info('[boot] hydrated from /api/bootstrap');
      } catch {
        if (isStoreFallbackEnabled()) {
          // Standalone / tests: no backend — hydrate from local fixtures.
          const mod = await import('@/data/fixtures');
          f = mod.buildFixtures();
          console.info('[boot] backend unreachable — hydrated from local fixtures');
        } else {
          // Served app: DB-authoritative. Do NOT resurrect fixtures. Mark the
          // stage initialised-but-empty, surface a reconnecting state, and
          // retry bootstrap on a backoff until the API answers.
          useConnectionStatus.getState().markReconnecting();
          set({ initialised: true, bootUnreachable: true });
          scheduleBootstrapRetry();
          return; // the outer `finally` still clears `hydrating`
        }
      }
      // A re-hydrate is server truth (bootstrap), not local edits. Apply it WITHOUT
      // the bridge re-PUTting every record back (tv-k9b.14 / tv-1vh.15): once the
      // bridge is installed (later boots / a reset), a plain re-hydrate would give
      // every record a fresh reference and re-emit the whole store. On the FIRST
      // boot the bridge isn't installed yet, so this is a harmless no-op wrapper.
      withoutWriteThrough(() => set(fixtureBundleToState(f)));
      set({ bootUnreachable: false });
      bootAttempts = 0;
      useConnectionStatus.getState().markOnline();

      // The journey fill is now BAKED into the seed snapshot (A2), so hydration
      // already carries the complete connected dataset. We deliberately run
      // NOTHING on top — re-running the replay here would upsert journey records
      // over the user's persisted edits and undo them. Only derive todayStats
      // from the hydrated records (a pure derivation, not a mutation of records).
      get().recomputeTodayStats();

      // Write-through: from here on, user mutations persist to the API. Installed
      // AFTER hydration + replay so the loaded world is the baseline (no storm).
      // Best-effort: a module-init cycle (only ever seen in the test harness, not
      // the browser) must never turn hydration into an unhandled rejection.
      try {
        const mod = await import('./persistenceBridge');
        mod.installPersistenceBridge?.();
      } catch {
        // persistence bridge unavailable — hydration + local state still work.
      }
     } finally {
       // Cleared on completion so an intentional reset can re-hydrate (tv-1vh.15).
       hydrating = false;
     }
    })();
  },
  recomputeTodayStats: () =>
    set((s) => ({
      todayStats: deriveTodayStats({
        deposits: s.deposits,
        confirmationCalls: s.confirmationCalls,
        customers: s.customers,
        bookings: s.bookings,
        salePayments: s.salePayments,
        todayIso: useSimulationStore.getState().clockIso,
      }),
    })),
  upsertCustomer: (c) =>
    set((s) => {
      // tv-pzvy: a changed phone drops this customer's old key, so the old number
      // no longer resolves to them.
      const byPhone = { ...s.customerIdsByPhone };
      const oldKey = s.customers[c.id]?.phone?.replace(/\s+/g, '');
      const newKey = (c.phone ?? '').replace(/\s+/g, '');
      if (oldKey !== undefined && oldKey !== newKey && byPhone[oldKey] === c.id) delete byPhone[oldKey];
      byPhone[newKey] = c.id;
      return { customers: { ...s.customers, [c.id]: c }, customerIdsByPhone: byPhone };
    }),
  updateMedicalProfile: (customerId, profile, updatedBy) =>
    set((s) => {
      const existing = s.customers[customerId];
      if (!existing) return s;
      const stamped: MedicalProfile = {
        ...profile,
        lastUpdatedAt: nowIso(),
        lastUpdatedBy: updatedBy ?? 'System',
      };
      return {
        customers: {
          ...s.customers,
          [customerId]: { ...existing, medicalProfile: stamped },
        },
      };
    }),
  setCustomerPreferences: (customerId, patch) =>
    set((s) => {
      const existing = s.customers[customerId];
      if (!existing) return s;
      return {
        customers: {
          ...s.customers,
          [customerId]: { ...existing, ...patch },
        },
      };
    }),
  updateDesignIntent: (customerId, patch) =>
    set((s) => {
      const existing = s.customers[customerId];
      if (!existing) return s;
      // A first brief edit (size, notes, a photo) starts an intent WITHOUT a
      // style or size: the glance must not claim "fine line" nobody chose (B8).
      const base: DesignIntent = existing.designIntent ?? {
        placement: '',
        estimatedHours: 0,
        referencePhotos: [],
      };
      return {
        customers: {
          ...s.customers,
          [customerId]: { ...existing, designIntent: { ...base, ...patch } },
        },
      };
    }),
  addConversationEntry: (customerId, entry) =>
    set((s) => {
      const existing = s.customers[customerId];
      if (!existing) return s;
      // tv-9nae: stamped strictly after the thread's last entry, so the demo
      // clock (frozen inside a day) never leaves two entries at one moment. A
      // server-authored entry keeps the server's time (store and server agree).
      const timestamp =
        entry.origin === 'server' ? entry.timestamp : nextThreadTimestamp(threadLastTimestamp(existing.conversation), entry.timestamp);
      return {
        customers: {
          ...s.customers,
          [customerId]: {
            ...existing,
            conversation: [...existing.conversation, timestamp === entry.timestamp ? entry : { ...entry, timestamp }],
          },
        },
      };
    }),
  addRapportNote: (customerId, note, author = 'You', atIso) =>
    set((s) => {
      const existing = s.customers[customerId];
      if (!existing) return s;
      const rapportNote = {
        id: `rapport-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
        note,
        author,
        timestamp: atIso ?? nowIso(),
      };
      return {
        customers: {
          ...s.customers,
          [customerId]: {
            ...existing,
            rapportNotes: [...existing.rapportNotes, rapportNote],
          },
        },
      };
    }),
  addFutureReadinessSignal: (sig) =>
    set((s) => {
      const existing = s.customers[sig.customerId];
      return {
        futureSignals: { ...s.futureSignals, [sig.id]: sig },
        customers: existing
          ? {
              ...s.customers,
              [sig.customerId]: {
                ...existing,
                futureReadinessSignals: [...existing.futureReadinessSignals, sig],
              },
            }
          : s.customers,
      };
    }),
  upsertBooking: (b) =>
    set((s) => {
      const existingCustomer = s.customers[b.customerId];
      const customers = existingCustomer && !existingCustomer.bookings.includes(b.id)
        ? {
            ...s.customers,
            [b.customerId]: {
              ...existingCustomer,
              bookings: [...existingCustomer.bookings, b.id],
            },
          }
        : s.customers;
      return { bookings: { ...s.bookings, [b.id]: b }, customers };
    }),
  applyReschedule: (args) =>
    set((s) => {
      const original = s.bookings[args.originalId];
      if (!original) return s;
      const toIso = args.mode === 'move' ? args.newStartIso : original.start;
      const historyEntry = {
        fromIso: original.start,
        toIso,
        reason: args.reason,
        note: args.note,
        by: args.by,
        at: args.at,
      };
      const history = [...(original.rescheduleHistory ?? []), historyEntry];
      const superseded: Booking = {
        ...original,
        status: 'superseded',
        rescheduleHistory: history,
        ...(args.mode === 'move' ? { rescheduledToBookingId: args.newBookingId } : {}),
      };
      const bookings = { ...s.bookings, [args.originalId]: superseded };
      let customers = s.customers;
      let deposits = s.deposits;
      let topupPlans = s.topupPlans;

      if (args.mode === 'move') {
        // The new slot KEEPS the lifecycle status (W4: 'rescheduled' is never
        // written; a legacy 'rescheduled' original is a booked slot -> 'confirmed')
        // and ALWAYS re-enters the confirmation cadence (resetCadence: call
        // outcome cleared, attempts 0, Ops flag cleared — DECIDED 23 Sep). Per-
        // session stamps do not carry (it has not been checked-in/seated/completed).
        // Mirrors the server's POST /api/intents/reschedule (intents-reschedule.ts).
        bookings[args.newBookingId] = resetCadence({
          ...original,
          id: args.newBookingId,
          artistId: args.artistId,
          // A move to another studio's artist books under their studio (B7).
          studioId: s.artists[args.artistId]?.studioId ?? original.studioId,
          start: args.newStartIso,
          status: original.status === 'rescheduled' ? 'confirmed' : original.status,
          rescheduledFromBookingId: args.originalId,
          rescheduledToBookingId: undefined,
          rescheduleHistory: history,
          checkedInAt: undefined,
          seatedAt: undefined,
          checkedOutAt: undefined,
          actualStart: undefined,
        });
        const cust = s.customers[original.customerId];
        if (cust && !cust.bookings.includes(args.newBookingId)) {
          customers = {
            ...s.customers,
            [original.customerId]: { ...cust, bookings: [...cust.bookings, args.newBookingId] },
          };
        }
        // Repoint the deposit from the superseded record to the new one so it
        // stays linked to a live dated booking (no double-count, no orphan): a
        // placed deposit by bookingId, a pending hold's by heldBookingId.
        if (original.depositId) {
          const dep = s.deposits[original.depositId];
          if (dep && (dep.bookingId === args.originalId || dep.heldBookingId === args.originalId)) {
            deposits = {
              ...s.deposits,
              [dep.id]: {
                ...dep,
                ...(dep.bookingId === args.originalId ? { bookingId: args.newBookingId } : {}),
                ...(dep.heldBookingId === args.originalId ? { heldBookingId: args.newBookingId } : {}),
              },
            };
          }
        }
        // A top-up plan on the original follows the live booking too (tv-2na.11.13),
        // mirroring the server's move.
        for (const p of Object.values(s.topupPlans)) {
          if (p.bookingId === args.originalId) {
            topupPlans = { ...topupPlans, [p.id]: { ...p, bookingId: args.newBookingId } };
          }
        }
      } else {
        // release ("No date", tv-2na.11.13): the money stays ON FILE with a fresh
        // 12-month validity — ALWAYS, even inside 7 days (DECIDED 24 Sep, Q6) —
        // and its top-up plan is cancelled (Q2). The ONE rule the server's
        // POST /api/intents/reschedule { mode: 'release' } runs.
        const dep = original.depositId ? s.deposits[original.depositId] : undefined;
        const active = Object.values(s.topupPlans).filter((p) => p.status === 'active');
        const plan =
          (dep ? active.find((p) => p.depositId === dep.id) : undefined) ??
          active.find((p) => p.bookingId === args.originalId);
        const release = applyNoDateRelease<Booking, Deposit, TopupPlan>({
          booking: original,
          deposit: dep,
          ...(plan ? { plan } : {}),
          atIso: args.at,
        });
        if (release.deposit) deposits = { ...s.deposits, [release.deposit.id]: release.deposit };
        if (release.plan) topupPlans = { ...s.topupPlans, [release.plan.id]: release.plan };
      }
      return { bookings, customers, deposits, topupPlans };
    }),
  dismissFlagSuggestion: (bookingId, kind) =>
    set((s) => {
      const existing = s.bookings[bookingId];
      if (!existing) return s;
      const current = existing.dismissedFlagSuggestions ?? [];
      if (current.includes(kind)) return s;
      return {
        bookings: {
          ...s.bookings,
          [bookingId]: { ...existing, dismissedFlagSuggestions: [...current, kind] },
        },
      };
    }),
  upsertArtist: (a) => set((s) => ({ artists: { ...s.artists, [a.id]: a } })),
  upsertStudio: (st) => set((s) => ({ studios: { ...s.studios, [st.id]: st } })),
  upsertAgent: (a) => set((s) => ({ agents: { ...s.agents, [a.id]: a } })),
  setRolePermissions: (next) => set({ rolePermissions: next }),
  setArtistTarget: (artistId, patch) =>
    set((s) => {
      const current = s.reportingTargets.artistOverrides[artistId] ?? s.reportingTargets.studioDefault;
      return {
        reportingTargets: {
          ...s.reportingTargets,
          artistOverrides: {
            ...s.reportingTargets.artistOverrides,
            [artistId]: { ...current, ...patch },
          },
        },
      };
    }),
  setStudioReviewsTarget: (studioId, reviewsPerWeek) =>
    set((s) => ({
      reportingTargets: {
        ...s.reportingTargets,
        reviewsByStudio: { ...s.reportingTargets.reviewsByStudio, [studioId]: reviewsPerWeek },
      },
    })),
  upsertDeposit: (d) => set((s) => ({ deposits: { ...s.deposits, [d.id]: d } })),
  upsertTopupPlan: (p) => set((s) => ({ topupPlans: { ...s.topupPlans, [p.id]: p } })),
  upsertWaiver: (w) =>
    set((s) => {
      const existingCustomer = s.customers[w.customerId];
      const alreadyLinked = existingCustomer?.waivers?.includes(w.id);
      const customers = existingCustomer && !alreadyLinked
        ? {
            ...s.customers,
            [w.customerId]: {
              ...existingCustomer,
              waivers: [...(existingCustomer.waivers ?? []), w.id],
            },
          }
        : s.customers;
      return { waivers: { ...s.waivers, [w.id]: w }, customers };
    }),
  absorbServerTruth: (server) => {
    const patch = absorbServerTruthInto(get(), server, (entity, id) => isWritePending(entity, id));
    if (patch) withoutWriteThrough(() => set(patch));
  },
  absorbServerCustomers: (server, fetchStartedAtMs) => {
    const patch = absorbServerCustomersInto(get(), server, (id) => isWritePending('customers', id), fetchStartedAtMs);
    if (patch) withoutWriteThrough(() => set(patch));
  },
  absorbServerSequenceState: (ids, server, isPending) => {
    const current = get().sequenceState;
    let next: Record<string, SequenceState> | null = null;
    for (const id of ids) {
      if (isPending(id)) continue;
      const row = server[id];
      if (row) {
        if (JSON.stringify(current[id]) === JSON.stringify(row)) continue;
        next ??= { ...current };
        next[id] = row;
      } else if (id in current) {
        next ??= { ...current };
        delete next[id];
      }
    }
    if (next) {
      const sequenceState = next;
      withoutWriteThrough(() => set({ sequenceState }));
    }
  },
  dropServerDeletedCustomer: (id) => {
    if (!get().customers[id]) return;
    withoutWriteThrough(() =>
      set((s) => {
        const customers = { ...s.customers };
        delete customers[id];
        return { customers };
      }),
    );
  },
  upsertPiece: (p) => set((s) => ({ pieces: { ...s.pieces, [p.id]: p } })),
  addPieceProgressPhoto: (pieceId, photo) =>
    set((s) => {
      const existing = s.pieces[pieceId];
      if (!existing) return s;
      const capturedAt = photo.capturedAt ?? nowIso();
      const taken = new Set((existing.progressPhotos ?? []).map((p) => p.id));
      const entry = {
        id: progressPhotoId(pieceId, capturedAt, photo.url, photo.kind, taken),
        url: photo.url,
        capturedAt,
        bookingId: photo.bookingId,
        caption: photo.caption,
        kind: photo.kind,
      };
      return {
        pieces: {
          ...s.pieces,
          [pieceId]: {
            ...existing,
            progressPhotos: [...(existing.progressPhotos ?? []), entry],
          },
        },
      };
    }),
  upsertFutureSignal: (sig) =>
    set((s) => ({ futureSignals: { ...s.futureSignals, [sig.id]: sig } })),
  upsertSelfBookLink: (l) =>
    set((s) => ({ selfBookLinks: { ...s.selfBookLinks, [selfBookLinkKey(s.selfBookLinks, l.token)]: l } })),
  upsertConfirmationCall: (c) =>
    set((s) => ({ confirmationCalls: { ...s.confirmationCalls, [c.id]: c } })),
  upsertNurtureTouch: (t) =>
    set((s) => ({ nurtureTouches: { ...s.nurtureTouches, [t.id]: t } })),
  upsertNurtureInbound: (i) =>
    set((s) => ({ nurtureInbounds: { ...s.nurtureInbounds, [i.id]: i } })),
  resolveNurtureInbound: (id, atIso) =>
    set((s) => {
      const existing = s.nurtureInbounds[id];
      if (!existing) return {};
      return { nurtureInbounds: { ...s.nurtureInbounds, [id]: { ...existing, handledAt: atIso } } };
    }),
  setNurtureTemplate: (segment, body) =>
    set((s) => ({ nurtureTemplates: { ...s.nurtureTemplates, [segment]: body } })),
  setReconResolution: (r) =>
    set((s) => ({ reconResolutions: { ...s.reconResolutions, [r.rowId]: r } })),
  removeReconResolution: (rowId) =>
    set((s) => {
      const next = { ...s.reconResolutions };
      delete next[rowId];
      return { reconResolutions: next };
    }),
  appendActivity: (a) => {
    if (seedSilent()) return;
    set((s) => ({ activityFeed: [a, ...s.activityFeed].slice(0, 200) }));
  },
  incStat: (key, by = 1) => {
    if (seedSilent()) return;
    set((s) => ({ todayStats: { ...s.todayStats, [key]: s.todayStats[key] + by } }));
  },
  sendChannelMessage: (channelId, body, opts = {}) =>
    set((s) => {
      const channel = s.internalComms[channelId];
      if (!channel) return s;
      const msg: InternalCommsMessage = {
        id: `msg-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
        authorId: opts.authorId ?? 'system-bot',
        body,
        timestamp: opts.timestamp ?? nowIso(),
        parentMessageId: opts.parentMessageId,
        mentions: opts.mentions,
        customerRefId: opts.customerRefId,
      };
      return {
        internalComms: {
          ...s.internalComms,
          [channelId]: { ...channel, messages: [...channel.messages, msg] },
        },
      };
    }),
  sendDmMessage: (threadId, body, opts = {}) =>
    set((s) => {
      const thread = s.internalDmThreads[threadId];
      if (!thread) return s;
      const msg: InternalCommsMessage = {
        id: `dm-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
        authorId: opts.authorId ?? 'system-bot',
        body,
        timestamp: opts.timestamp ?? nowIso(),
        mentions: opts.mentions,
        customerRefId: opts.customerRefId,
        ...(opts.attachments?.length ? { attachments: opts.attachments } : {}),
      };
      return {
        internalDmThreads: {
          ...s.internalDmThreads,
          [threadId]: { ...thread, messages: [...thread.messages, msg] },
        },
      };
    }),
  openOrCreateDm: (participantIds, creatorId, nowIso) => {
    const sorted = [...new Set(participantIds)].sort();
    const state = get();
    const existing = Object.values(state.internalDmThreads).find((t) => {
      const a = [...t.participantIds].sort();
      return a.length === sorted.length && a.every((id, i) => id === sorted[i]);
    });
    if (existing) return existing.id;
    const id = `dm-thread-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
    const thread: InternalDmThread = {
      id,
      participantIds: sorted,
      createdAt: nowIso,
      messages: [],
    };
    set((s) => ({
      internalDmThreads: { ...s.internalDmThreads, [id]: thread },
    }));
    void creatorId;
    return id;
  },
  createChannel: (name, memberIds, creatorId, opts = {}) => {
    const id = `channel-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
    // The creator is always a member so it appears in their own list immediately.
    const members = [...new Set([creatorId, ...memberIds])];
    const channel: InternalCommsChannel = {
      id,
      name: name.trim(),
      scope: 'network',
      memberIds: members,
      messages: [],
      ...(opts.description ? { description: opts.description } : {}),
      ...(opts.studioId ? { studioId: opts.studioId } : {}),
    };
    set((s) => ({
      internalComms: { ...s.internalComms, [id]: channel },
    }));
    return id;
  },
  addReaction: (scope, messageId, emoji, byPersonId) =>
    set((s) => {
      const applyToMessages = (msgs: InternalCommsMessage[]): InternalCommsMessage[] =>
        msgs.map((m) => {
          if (m.id !== messageId) return m;
          const existing = m.reactions ?? [];
          const idx = existing.findIndex(
            (r) => r.emoji === emoji && r.byPersonId === byPersonId,
          );
          const next = idx >= 0
            ? existing.filter((_, i) => i !== idx)
            : [...existing, { emoji, byPersonId }];
          return { ...m, reactions: next };
        });
      if (scope.kind === 'channel') {
        const ch = s.internalComms[scope.containerId];
        if (!ch) return s;
        return {
          internalComms: {
            ...s.internalComms,
            [scope.containerId]: { ...ch, messages: applyToMessages(ch.messages) },
          },
        };
      }
      const dm = s.internalDmThreads[scope.containerId];
      if (!dm) return s;
      return {
        internalDmThreads: {
          ...s.internalDmThreads,
          [scope.containerId]: { ...dm, messages: applyToMessages(dm.messages) },
        },
      };
    }),
  addAutoPing: (channelId, body, opts = {}) => {
    if (seedSilent()) return;
    set((s) => {
      const channel = s.internalComms[channelId];
      if (!channel) return s;
      const msg: InternalCommsMessage = {
        id: `ping-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
        authorId: 'system-bot',
        body,
        timestamp: opts.timestamp ?? nowIso(),
        autoWinPing: true,
        customerRefId: opts.customerRefId,
        mentions: opts.mentions,
      };
      return {
        internalComms: {
          ...s.internalComms,
          [channelId]: { ...channel, messages: [...channel.messages, msg] },
        },
      };
    });
  },
  setRebookOutcome: (bookingId, outcome, attribution) =>
    set((s) => {
      const booking = s.bookings[bookingId];
      if (!booking) return s;
      return {
        bookings: {
          ...s.bookings,
          [bookingId]: { ...booking, rebookOutcome: outcome, rebookAttribution: attribution },
        },
      };
    }),
  upsertRebookFollowup: (task) =>
    set((s) => ({
      rebookFollowupTasks: { ...s.rebookFollowupTasks, [task.id]: task },
    })),
  upsertHealingCheck: (task) =>
    set((s) => ({
      healingCheckTasks: { ...s.healingCheckTasks, [task.id]: task },
    })),
  upsertNoRebookFlag: (flag) =>
    set((s) => ({
      noRebookFlags: { ...s.noRebookFlags, [flag.id]: flag },
    })),
  upsertNoDepositDeclineFlag: (flag) =>
    set((s) => ({
      noDepositDeclineFlags: { ...s.noDepositDeclineFlags, [flag.id]: flag },
    })),
  upsertPaybyExtensionRequest: (req) =>
    set((s) => ({ paybyExtensionRequests: { ...s.paybyExtensionRequests, [req.id]: req } })),
  upsertSweeperOutcome: (outcome) =>
    set((s) => ({ sweeperOutcomes: { ...s.sweeperOutcomes, [outcome.id]: outcome } })),
  upsertOverrideRequest: (request) =>
    set((s) => ({
      overrideRequests: { ...s.overrideRequests, [request.id]: request },
    })),
  upsertSalePayment: (payment) =>
    set((s) => ({ salePayments: { ...s.salePayments, [payment.id]: payment } })),
  upsertPayoutRecord: (record) =>
    set((s) => ({ payoutRecords: { ...s.payoutRecords, [record.id]: record } })),
  upsertCashLedger: (ledger) =>
    set((s) => ({ cashLedgers: { ...s.cashLedgers, [ledger.id]: ledger } })),
  upsertMerchItem: (item) =>
    set((s) => ({ merchItems: { ...s.merchItems, [item.id]: item } })),
  upsertDayPack: (pack) =>
    set((s) => ({ dayPacks: { ...s.dayPacks, [pack.id]: pack } })),
  upsertDocketLine: (line) =>
    set((s) => ({ docketLines: { ...s.docketLines, [line.id]: line } })),
  upsertSettlementLump: (lump) =>
    set((s) => ({ settlementLumps: { ...s.settlementLumps, [lump.id]: lump } })),
  setSequenceState: (customerId, state) =>
    set((s) => ({
      sequenceState: { ...s.sequenceState, [customerId]: state },
    })),
  patchSequenceState: (customerId, patch) =>
    set((s) => {
      const existing = s.sequenceState[customerId] ?? { stage: 1 as const };
      return {
        sequenceState: {
          ...s.sequenceState,
          [customerId]: { ...existing, ...patch },
        },
      };
    }),
  clearSequenceState: (customerId) =>
    set((s) => {
      if (!(customerId in s.sequenceState)) return s;
      const next = { ...s.sequenceState };
      delete next[customerId];
      return { sequenceState: next };
    }),
  resolveRebookFollowup: (taskId, resolution) =>
    set((s) => {
      const task = s.rebookFollowupTasks[taskId];
      if (!task) return s;
      const updatedTask: RebookFollowupTask = {
        ...task,
        resolvedAt: resolution.resolvedAt,
        resolvedOutcome: resolution.outcome,
      };
      // Side-effect: patch the source booking's outcome so the leaderboard
      // and reporting view see the resolved state too.
      const source = s.bookings[task.sourceBookingId];
      const nextBookings = source && source.rebookOutcome?.kind === 'rebookDeferredToSales'
        ? {
            ...s.bookings,
            [task.sourceBookingId]: {
              ...source,
              rebookOutcome: {
                ...source.rebookOutcome,
                resolvedAt: resolution.resolvedAt,
                resolvedOutcome: resolution.outcome,
              },
            },
          }
        : s.bookings;
      return {
        rebookFollowupTasks: { ...s.rebookFollowupTasks, [taskId]: updatedTask },
        bookings: nextBookings,
      };
    }),
  reset: () => {
    hydrating = false; // let a post-reset loadFixtures re-hydrate (tv-1vh.15)
    set({
      initialised: false,
      bootUnreachable: false,
      customers: {},
      customerIdsByPhone: {},
      bookings: {},
      deposits: {},
      topupPlans: {},
      waivers: {},
      pieces: {},
      futureSignals: {},
      selfBookLinks: {},
      marketingCampaigns: {},
      confirmationCalls: {},
      internalComms: {},
      internalDmThreads: {},
      rebookFollowupTasks: {},
      healingCheckTasks: {},
      noRebookFlags: {},
      noDepositDeclineFlags: {},
      paybyExtensionRequests: {},
      sweeperOutcomes: {},
      overrideRequests: {},
      reconResolutions: {},
      sequenceState: {},
      salePayments: {},
      payoutRecords: {},
      cashLedgers: {},
      merchItems: {},
      docketLines: {},
      settlementLandings: {},
      depositLandings: {},
      settlementLumps: {},
      dayPacks: {},
      studios: {},
      artists: {},
      agents: {},
      rolePermissions: DEFAULT_ROLE_PERMISSIONS,
      reportingTargets: DEFAULT_REPORTING_TARGETS,
      tailRecords: [],
      activityFeed: [],
      todayStats: { ...emptyStats },
    });
  },
}));

export type BrainStore = typeof useBrainStore;

// --- Cross-tab sync -------------------------------------------------------
// Mirror brain mutations across tabs over a BroadcastChannel so marking a
// booking completed on the floor tab reflects in the office tab (and back)
// without a server round-trip. No React involved — this is store ↔ store.
//
// Each message carries only the records the sending tab changed itself (B2:
// 8e8e), merged by id on the receiving side, so a stale tab can no longer
// replace a fresher one's store. Applying a delta never re-broadcasts it (see
// `applyingRemote`).

// Versioned channel key. Bumped for the seed rebuild (real 11-studio network) so
// any tab still holding demo state keyed to the old fictional studio ids cannot
// broadcast it into a tab running the realigned seed.
// v3 (B2): record deltas, not whole-store snapshots; never mixes with a v2 tab.
const STORE_CHANNEL = 'addikted-to-ink-store-v3-delta';
const BROADCAST_THROTTLE_MS = 200;

interface DeltaMessage {
  type: 'delta';
  delta: BroadcastDelta;
}

/**
 * A broadcast from an UNHYDRATED tab — a tokenless customer-flow page (the public
 * pay page) or a tab mid-boot that hit /api/bootstrap → 401 → EMPTY store — carries
 * empty core collections. Applying it as a full-collection replace would WIPE a
 * populated tab and make the persistence bridge DELETE every record (tv-k9b.14,
 * the "resurrects/vanishes" delete-storm). Treat any payload whose customers,
 * bookings AND deposits are all empty/absent as noise and ignore it — a genuinely
 * hydrated tab always carries hundreds of each.
 */
/** The record collections a live view bundle (floor/calendar/confirmations/…) carries. */
export interface ServerTruth {
  bookings?: Record<string, Booking>;
  deposits?: Record<string, Deposit>;
  waivers?: Record<string, Waiver>;
  /** ontheday-20 (b): sales taken on another device (their cash is in the drawer). */
  salePayments?: Record<string, SalePayment>;
}

const UNSOLID_PRE_ARRIVAL: ReadonlySet<string> = new Set(['held', 'pending-deposit']);

/** The day's forward order: a booking only ever moves right along it. */
const DAY_ORDER: Record<string, number> = {
  held: 0,
  'pending-deposit': 0,
  confirmed: 1,
  'checked-in': 2,
  'in-chair': 3,
  completed: 4,
};

/** The server holds the booking further along the day (arrived or later) than this tab does. */
export function isForwardMove(local: string, server: string): boolean {
  const from = DAY_ORDER[local];
  const to = DAY_ORDER[server];
  return from !== undefined && to !== undefined && to > from && to >= 2;
}

/**
 * tv-2na.11.4 / tv-2na.11.5: out-of-band writes (a customer signing a waiver or
 * paying a deposit on their own device) reach a staff tab only through /ws ->
 * view refetch -> the query cache. The brain store, which the check-in gate and
 * the write paths read, never learned of them, so the Floor showed "signed"
 * while check-in still refused. This folds a view's records into the store,
 * FORWARD-ONLY, so it can never undo a local edit that has not landed yet:
 *   waiver   missing locally, or the server's is signed and ours is not;
 *   deposit  ours is pending and the server's is paid;
 *   booking  ours is an unsolid hold (held / pending deposit) and the server's is
 *            solid (the pay firmed it).
 * Returns the patch (null when nothing moves). Pure.
 */
export function absorbServerTruthInto(
  state: Pick<BrainState, 'bookings' | 'deposits' | 'waivers' | 'customers'> & Partial<Pick<BrainState, 'salePayments'>>,
  server: ServerTruth,
  /** A local write still queued for this record: never absorb over it (calendar-11). */
  isPending: (entity: 'bookings' | 'deposits' | 'salePayments', id: string) => boolean = () => false,
): Partial<Pick<BrainState, 'bookings' | 'deposits' | 'waivers' | 'customers' | 'salePayments'>> | null {
  let waivers: Record<string, Waiver> | undefined;
  let customers: BrainState['customers'] | undefined;
  for (const w of Object.values(server.waivers ?? {})) {
    const local = state.waivers[w.id];
    if (local && (local.status === 'signed' || w.status !== 'signed')) continue;
    waivers ??= { ...state.waivers };
    waivers[w.id] = w;
    const cust = (customers ?? state.customers)[w.customerId];
    if (cust && !cust.waivers?.includes(w.id)) {
      customers ??= { ...state.customers };
      customers[w.customerId] = { ...cust, waivers: [...(cust.waivers ?? []), w.id] };
    }
  }

  let deposits: Record<string, Deposit> | undefined;
  for (const d of Object.values(server.deposits ?? {})) {
    const local = state.deposits[d.id];
    // calendar-11 (b): a deposit this tab never loaded (made on another device) is
    // taken as the server has it, unless a local write for it is still queued.
    const missing = !local && !isPending('deposits', d.id);
    // sweeper-10: a cancel elsewhere that kept the money on file unlinks a paid
    // deposit from its booking; take that in, or the Sweeper can't place it.
    const unlinked =
      !!local && local.status === 'paid' && d.status === 'paid' && !!local.bookingId && !d.bookingId &&
      !isPending('deposits', d.id);
    if (!missing && !unlinked && (!local || local.status !== 'pending' || d.status !== 'paid')) continue;
    deposits ??= { ...state.deposits };
    deposits[d.id] = d;
  }

  let bookings: Record<string, Booking> | undefined;
  for (const b of Object.values(server.bookings ?? {})) {
    const local = state.bookings[b.id];
    // calendar-11 (b, c): a booking this tab never loaded is taken in (the Floor's
    // Check in found "not-found" until the record was opened elsewhere), and a
    // booking the server has moved FORWARD along the day (checked in / seated /
    // done on another device) takes the server's copy, unless a local write for
    // it is still queued.
    if (!isPending('bookings', b.id) && (!local || isForwardMove(local.status, b.status))) {
      bookings ??= { ...state.bookings };
      bookings[b.id] = b;
      continue;
    }
    if (!local || !UNSOLID_PRE_ARRIVAL.has(local.status) || !isSolid(b)) continue;
    bookings ??= { ...state.bookings };
    bookings[b.id] = b;
  }

  // ontheday-20 (b): a sale this tab never saw (taken on another device) is taken
  // in, so the cash steer counts the cash it put in the drawer. A sale is written
  // once (the checkout intent), so only missing ones move.
  let salePayments: BrainState['salePayments'] | undefined;
  if (state.salePayments) {
    for (const sp of Object.values(server.salePayments ?? {})) {
      if (state.salePayments[sp.id] || isPending('salePayments', sp.id)) continue;
      salePayments ??= { ...state.salePayments };
      salePayments[sp.id] = sp;
    }
  }

  if (!waivers && !deposits && !bookings && !salePayments) return null;
  return {
    ...(salePayments ? { salePayments } : {}),
    ...(waivers ? { waivers } : {}),
    ...(customers ? { customers } : {}),
    ...(deposits ? { deposits } : {}),
    ...(bookings ? { bookings } : {}),
  };
}

/**
 * Merge two conversations by message id (the server's copy of a message wins), in
 * time order. With `fetchStartedAtMs`, a SERVER-OWNED local row the server no
 * longer has is dropped (a DM link moved it to another customer, tv-isee), unless
 * it is stamped at or after the read started: it may postdate the read.
 */
function mergeConversation(
  local: readonly ConversationEntry[],
  server: readonly ConversationEntry[],
  fetchStartedAtMs?: number,
): ConversationEntry[] {
  const byId = new Map<string, ConversationEntry>();
  const serverIds = new Set(server.map((m) => m.id));
  for (const m of local) {
    const moved =
      fetchStartedAtMs !== undefined &&
      m.origin === 'server' &&
      !serverIds.has(m.id) &&
      (Date.parse(m.timestamp) || 0) < fetchStartedAtMs;
    if (!moved) byId.set(m.id, m);
  }
  for (const m of server) byId.set(m.id, m);
  return [...byId.values()].sort((a, b) => a.timestamp.localeCompare(b.timestamp) || a.id.localeCompare(b.id));
}

/**
 * B2 (8e8d, 7gq9): fold the server's copy of customers the stream said changed
 * (the day-advance's reminder SMS, another device's message) into the store. The
 * conversation is append-only — merged by message id, so no message is ever lost
 * either way. The other fields take the server's copy unless `hasUnsavedWrite`
 * says this tab has its own edit to that customer still on its way. A live
 * refresh passes `fetchStartedAtMs` so rows a DM link moved away are dropped
 * (see mergeConversation). Returns the patch, or null when nothing moves. Pure.
 */
export function absorbServerCustomersInto(
  state: Pick<BrainState, 'customers'>,
  server: Record<string, CustomerRecord>,
  hasUnsavedWrite: (customerId: string) => boolean,
  fetchStartedAtMs?: number,
): { customers: BrainState['customers'] } | null {
  let customers: BrainState['customers'] | undefined;
  for (const [id, remote] of Object.entries(server)) {
    const local = state.customers[id];
    const base = local && hasUnsavedWrite(id) ? local : remote;
    const next: CustomerRecord = local
      ? { ...base, conversation: mergeConversation(local.conversation ?? [], remote.conversation ?? [], fetchStartedAtMs) }
      : remote;
    if (local && JSON.stringify(next) === JSON.stringify(local)) continue;
    customers ??= { ...state.customers };
    customers[id] = next;
  }
  return customers ? { customers } : null;
}

export function isUnhydratedStatePayload(payload: Partial<BrainState>): boolean {
  const isEmpty = (c: unknown) => !c || Object.keys(c as object).length === 0;
  return isEmpty(payload.customers) && isEmpty(payload.bookings) && isEmpty(payload.deposits);
}

/** Slices never broadcast: the actions, and `tailRecords` (a ~50k static seed loaded identically in every tab). */
const UNBROADCAST: ReadonlySet<string> = new Set(['tailRecords']);
const RECORD_COLLECTIONS: ReadonlySet<string> = new Set(PERSISTABLE_ENTITIES);
/** More removals than this in one delta are an unhydrated / wiped tab, never real deletes (tv-k9b.14). */
const BULK_REMOVE_GUARD = 25;

/**
 * B2 (8e8e): what one tab tells the others. Only the records it changed ITSELF
 * (null = removed), keyed by collection, plus any other slice it changed, whole.
 * Tabs used to broadcast their whole store and the receiver replaced its own when
 * the sender's per-page-load counter was higher, so a long-open stale tab always
 * beat a freshly reloaded one and reverted it.
 */
export interface BroadcastDelta {
  records: Record<string, Record<string, unknown | null>>;
  slices: Record<string, unknown>;
}

interface DirtySet {
  records: Map<string, Set<string>>;
  slices: Set<string>;
}

/** Record in `dirty` what moved between two store states (by reference). Pure but for `dirty`. */
export function collectBroadcastDelta(prev: BrainState, next: BrainState, dirty: DirtySet): void {
  const p = prev as unknown as Record<string, unknown>;
  const n = next as unknown as Record<string, unknown>;
  for (const key of Object.keys(n)) {
    const now = n[key];
    const was = p[key];
    if (now === was || typeof now === 'function' || UNBROADCAST.has(key)) continue;
    if (!RECORD_COLLECTIONS.has(key)) {
      dirty.slices.add(key);
      continue;
    }
    const nowColl = (now ?? {}) as Record<string, unknown>;
    const wasColl = (was ?? {}) as Record<string, unknown>;
    let ids = dirty.records.get(key);
    for (const id in nowColl) {
      if (nowColl[id] !== wasColl[id]) (ids ??= new Set()).add(id);
    }
    for (const id in wasColl) {
      if (!(id in nowColl)) (ids ??= new Set()).add(id);
    }
    if (ids) dirty.records.set(key, ids);
  }
}

/** The delta to send for what is dirty, read from the current state. */
function buildBroadcastDelta(state: BrainState, dirty: DirtySet): BroadcastDelta {
  const cur = state as unknown as Record<string, unknown>;
  const records: BroadcastDelta['records'] = {};
  for (const [key, ids] of dirty.records) {
    const coll = (cur[key] ?? {}) as Record<string, unknown>;
    records[key] = Object.fromEntries([...ids].map((id) => [id, coll[id] ?? null]));
  }
  const slices = Object.fromEntries([...dirty.slices].map((k) => [k, cur[k]]));
  return { records, slices };
}

/**
 * Merge another tab's delta into this tab's state. Records are merged by id; a
 * record this tab has its own unsaved write for is left alone; a customer's
 * conversation is merged by message id so an older tab's copy never drops a
 * message. A mass removal is ignored as an unhydrated tab (tv-k9b.14). Returns
 * the patch, or null when nothing moves. Pure.
 */
export function applyBroadcastDelta(
  state: BrainState,
  delta: BroadcastDelta,
  hasUnsavedWrite: (entity: string, id: string) => boolean,
): Partial<BrainState> | null {
  const cur = state as unknown as Record<string, unknown>;
  const patch: Record<string, unknown> = {};
  for (const [key, recs] of Object.entries(delta.records ?? {})) {
    if (!RECORD_COLLECTIONS.has(key)) continue;
    const removals = Object.values(recs).filter((r) => r === null).length;
    const guardRemovals = removals > BULK_REMOVE_GUARD;
    let coll: Record<string, unknown> | undefined;
    const base = (cur[key] ?? {}) as Record<string, unknown>;
    if (key === 'customers') {
      const incoming = Object.fromEntries(Object.entries(recs).filter(([, r]) => r !== null)) as Record<string, CustomerRecord>;
      const merged = absorbServerCustomersInto(
        { customers: base as BrainState['customers'] },
        incoming,
        (id) => hasUnsavedWrite('customers', id),
      );
      if (merged) coll = { ...merged.customers };
    }
    for (const [id, rec] of Object.entries(recs)) {
      if (key === 'customers' && rec !== null) continue;
      if (hasUnsavedWrite(key, id)) continue;
      if (rec === null) {
        if (guardRemovals || !(id in base)) continue;
        coll ??= { ...base };
        delete coll[id];
      } else if (base[id] !== rec) {
        coll ??= { ...base };
        coll[id] = rec;
      }
    }
    if (coll) patch[key] = coll;
  }
  for (const [key, value] of Object.entries(delta.slices ?? {})) {
    if (UNBROADCAST.has(key) || RECORD_COLLECTIONS.has(key) || typeof cur[key] === 'function') continue;
    patch[key] = value;
  }
  return Object.keys(patch).length > 0 ? (patch as Partial<BrainState>) : null;
}

if (typeof BroadcastChannel !== 'undefined') {
  const channel = new BroadcastChannel(STORE_CHANNEL);
  let applyingRemote = false;
  let broadcastTimer: ReturnType<typeof setTimeout> | null = null;
  let dirty: DirtySet = { records: new Map(), slices: new Set() };

  const flush = () => {
    broadcastTimer = null;
    const sent = dirty;
    dirty = { records: new Map(), slices: new Set() };
    if (sent.records.size === 0 && sent.slices.size === 0) return;
    const message: DeltaMessage = { type: 'delta', delta: buildBroadcastDelta(useBrainStore.getState(), sent) };
    channel.postMessage(message);
  };

  useBrainStore.subscribe((state, prev) => {
    if (applyingRemote || state === prev) return;
    collectBroadcastDelta(prev, state, dirty);
    if (broadcastTimer === null) {
      broadcastTimer = setTimeout(flush, BROADCAST_THROTTLE_MS);
    }
  });

  channel.onmessage = (event: MessageEvent<DeltaMessage>) => {
    const message = event.data;
    if (!message || message.type !== 'delta' || !message.delta) return;
    const patch = applyBroadcastDelta(useBrainStore.getState(), message.delta, isWritePending);
    if (!patch) return;
    applyingRemote = true;
    try {
      // Another tab's own write, already on its way to the server: mirror it
      // WITHOUT the write-through bridge PUTting/DELETEing it back (tv-k9b.14).
      withoutWriteThrough(() => {
        useBrainStore.setState(patch);
      });
    } finally {
      applyingRemote = false;
    }
  };

  if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', () => {
      if (broadcastTimer !== null) {
        clearTimeout(broadcastTimer);
        flush();
      }
      channel.close();
    });
  }
}
